import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Result, Row, Col, Spin, Button, Typography } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { VersionInfo } from '../../utils/version';
import { CONTACT_CONFIG } from '../../config/contact';

const { Title, Text, Paragraph } = Typography;

interface ActivationResult {
  success: boolean;
  message: string;
  data?: {
    activated?: boolean;
    alreadyActivated?: boolean;
    username: string;
    email: string;
    companyName?: string;
    loginUrl?: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

const ActivateAccountPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState<ActivationResult | null>(null);
  const resolveLocalizedMessage = (
    backendMessage: string | undefined,
    fallback: string
  ) => {
    if (!backendMessage) return fallback;
    const isZh = i18n.language.toLowerCase().startsWith('zh');
    const hasChinese = /[\u4e00-\u9fff]/.test(backendMessage);
    return !isZh && hasChinese ? fallback : backendMessage;
  };

  // 自动激活账户
  useEffect(() => {
    const activateAccount = async () => {
      if (!token || !email) {
        setResult({
          success: false,
          message: t('auth:activate.linkInvalid'),
          error: {
            code: 'INVALID_LINK',
            message: t('auth:activate.linkInvalid'),
          },
        });
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch('/api/v1/auth/activate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-language': i18n.language || 'en',
            'Accept-Language': i18n.language || 'en',
          },
          body: JSON.stringify({
            token,
            email: decodeURIComponent(email),
          }),
        });

        const data = await response.json();
        setResult(data);
      } catch (error) {
        console.error('Activation error:', error);
        setResult({
          success: false,
          message: t('auth:activate.failed'),
          error: {
            code: 'NETWORK_ERROR',
            message: t('common:message.networkError'),
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    activateAccount();
  }, [token, email, i18n.language, t]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin
            indicator={
              <LoadingOutlined style={{ fontSize: 48, color: '#10b981' }} />
            }
          />
          <Title level={4} style={{ marginTop: 24, color: '#374151' }}>
            {t('auth:activate.activating')}
          </Title>
          <Text type="secondary">{t('common:status.loading')}</Text>
        </div>
      );
    }

    if (result?.success) {
      return (
        <Result
          icon={
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
              }}
            >
              <CheckCircleFilled style={{ fontSize: 40, color: '#fff' }} />
            </div>
          }
          title={
            <span style={{ color: '#065f46' }}>
              {result.data?.alreadyActivated
                ? t('auth:activate.alreadyActivated')
                : t('auth:activate.success')}
            </span>
          }
          subTitle={
            <div style={{ maxWidth: 400, margin: '0 auto' }}>
              <Paragraph style={{ color: '#6b7280', marginBottom: 24 }}>
                {resolveLocalizedMessage(
                  result.message,
                  t('auth:activate.successMessage')
                )}
              </Paragraph>

              {result.data && (
                <div
                  style={{
                    background: '#f0fdf4',
                    borderRadius: 12,
                    padding: '20px 24px',
                    textAlign: 'left',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  <Text
                    strong
                    style={{
                      color: '#166534',
                      display: 'block',
                      marginBottom: 12,
                    }}
                  >
                    {t('auth:activate.loginInfoTitle')}
                  </Text>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text style={{ color: '#6b7280' }}>
                        {t('auth:activate.usernameLabel')}
                      </Text>
                      <Text strong>{result.data.username}</Text>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Text style={{ color: '#6b7280' }}>
                        {t('auth:activate.emailLabel')}
                      </Text>
                      <Text strong>{result.data.email}</Text>
                    </div>
                    {result.data.companyName && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text style={{ color: '#6b7280' }}>
                          {t('auth:activate.companyLabel')}
                        </Text>
                        <Text strong>{result.data.companyName}</Text>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          }
          extra={
            <Button
              type="primary"
              size="large"
              onClick={() => navigate('/auth/login')}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderColor: 'transparent',
                borderRadius: 8,
                height: 48,
                paddingLeft: 48,
                paddingRight: 48,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              }}
            >
              {t('auth:activate.login')}
            </Button>
          }
        />
      );
    }

    // 错误状态
    return (
      <Result
        icon={
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
            }}
          >
            <CloseCircleFilled style={{ fontSize: 40, color: '#fff' }} />
          </div>
        }
        title={
          <span style={{ color: '#991b1b' }}>{t('auth:activate.failed')}</span>
        }
        subTitle={
          <div style={{ maxWidth: 400, margin: '0 auto' }}>
              <Paragraph style={{ color: '#6b7280' }}>
              {resolveLocalizedMessage(
                result?.error?.message || result?.message,
                t('auth:activate.linkInvalid')
              )}
              </Paragraph>

            <div
              style={{
                background: '#fef2f2',
                borderRadius: 12,
                padding: '16px 20px',
                textAlign: 'left',
                border: '1px solid #fecaca',
                marginTop: 16,
              }}
            >
              <Text style={{ color: '#991b1b', fontSize: 13, lineHeight: 1.6 }}>
                <strong>{t('auth:activate.possibleReasons')}</strong>
                <br />
                • {t('auth:activate.reasonExpired')}
                <br />
                • {t('auth:activate.reasonUsed')}
                <br />
                • {t('auth:activate.reasonIncomplete')}
                <br />
                <br />
                {t('auth:activate.needHelp', {
                  hotline: CONTACT_CONFIG.serviceHotline,
                })}
              </Text>
            </div>
          </div>
        }
        extra={[
          <Button
            key="login"
            type="primary"
            size="large"
            onClick={() => navigate('/auth/login')}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderColor: 'transparent',
              borderRadius: 8,
              height: 44,
              fontWeight: 600,
            }}
          >
            {t('auth:activate.login')}
          </Button>,
          <Button
            key="home"
            size="large"
            onClick={() => navigate('/')}
            style={{
              borderRadius: 8,
              height: 44,
            }}
          >
            {t('menu:home')}
          </Button>,
        ]}
      />
    );
  };

  return (
    <Row className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* 左侧装饰区域 */}
      <Col
        xs={0}
        md={12}
        className="relative"
        style={{
          background:
            'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '48px',
          overflow: 'hidden',
        }}
      >
        {/* 背景装饰 */}
        <div
          style={{
            position: 'absolute',
            top: '-10%',
            right: '-10%',
            width: '50%',
            height: '50%',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-5%',
            left: '-5%',
            width: '40%',
            height: '40%',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
          }}
        />

        {/* 内容 */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            maxWidth: 500,
          }}
        >
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: 24,
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px',
            }}
          >
            <MailOutlined style={{ fontSize: 48, color: '#fff' }} />
          </div>
          <Title level={2} style={{ color: '#fff', marginBottom: 16 }}>
            {t('auth:activate.title')}
          </Title>
          <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>
            {t('auth:activate.leftDescriptionLine1')}
            <br />
            {t('auth:activate.leftDescriptionLine2')}
          </Paragraph>
        </div>
      </Col>

      {/* 右侧内容区域 */}
      <Col
        xs={24}
        md={12}
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '48px 24px',
          minHeight: '100vh',
        }}
      >
        <div style={{ width: '100%', maxWidth: 500 }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              }}
            >
              <span style={{ color: '#fff', fontSize: 20, fontWeight: 700 }}>
                GB
              </span>
            </div>
            <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
              {t('auth:brand.name')}
            </Title>
            <Text type="secondary">Offshore Green Bond Express</Text>
          </div>

          {/* 主要内容卡片 */}
          <Card
            bordered={false}
            style={{
              borderRadius: 16,
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
            }}
            styles={{ body: { padding: '32px 24px' } }}
          >
            {renderContent()}
          </Card>

          {/* 底部链接 */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('auth:register.hasAccount')}{' '}
              <Link
                to="/auth/login"
                style={{ color: '#10b981', fontWeight: 500 }}
              >
                {t('auth:activate.login')}
              </Link>
            </Text>
          </div>

          {/* 版本信息 */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {VersionInfo.displayVersion}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('common:app.copyright', { year: new Date().getFullYear() })}
            </Text>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default ActivateAccountPage;
